import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "./store";
import {
  AlertState,
  ConfirmState,
  OptionsState,
  setAlert,
  setConfirm,
  setOptions,
} from "../../reducers/alerts";
import { ButtonVariants } from "../../components/common/Button/constants";

type OnConfirm = () => void | Promise<void>;

interface ConfirmOptions {
  confirmText?: string;
  cancelText?: string;
}

export function useCustomConfirm() {
  const dispatch = useAppDispatch();
  const onConfirmRef = useRef<OnConfirm | null>(null);
  const onCancelRef = useRef<OnConfirm | null>(null);
  const { confirm: confirmState } = useAppSelector((state) => state.alerts);

  const performConfirm = useCallback(async () => {
    await (onConfirmRef.current as OnConfirm)();
    onCancelRef.current = null;
    dispatch(setConfirm({ confirm: ConfirmState.OFF, message: null }));
  }, [dispatch]);

  useEffect(() => {
    if (
      onConfirmRef.current !== null &&
      confirmState == ConfirmState.CONFIRMED
    ) {
      void performConfirm();
    }
  }, [confirmState, performConfirm]);

  useEffect(() => {
    if (confirmState == ConfirmState.OFF) {
      if (onCancelRef.current !== null) {
        void onCancelRef.current();
      }
      onConfirmRef.current = null;
    }
  }, [confirmState]);

  return useCallback(
    (
      message: string,
      onConfirm: OnConfirm,
      onCancel?: OnConfirm,
      options?: ConfirmOptions,
    ) => {
      onConfirmRef.current = onConfirm;
      onCancelRef.current = onCancel || null;
      dispatch(
        setConfirm({
          confirm: ConfirmState.WAITING_FOR_CONFIRMATION,
          message,
          confirmText: options?.confirmText,
          cancelText: options?.cancelText,
        }),
      );
    },
    [dispatch],
  );
}

export function useCustomAlert() {
  const dispatch = useAppDispatch();
  const onAcknowledgeRef = useRef<OnConfirm | null>(null);
  const { alert: alertState } = useAppSelector((state) => state.alerts);

  useEffect(() => {
    if (alertState == AlertState.OFF) {
      if (onAcknowledgeRef.current !== null) {
        void onAcknowledgeRef.current();
      }
      onAcknowledgeRef.current = null;
    }
  }, [alertState]);

  return useCallback(
    (message: string, onAcknowledge?: OnConfirm) => {
      onAcknowledgeRef.current = onAcknowledge || null;
      dispatch(setAlert({ alert: AlertState.ALERTING, message }));
    },
    [dispatch],
  );
}

type OnSelect = () => void | Promise<void>;

interface CustomOption {
  text: string;
  onSelect: OnSelect;
  variant?: ButtonVariants;
}

export function useCustomOptions() {
  const optionCallbacksRef = useRef<OnSelect[]>([]);
  const onCancelRef = useRef<OnSelect | null>(null);

  const { options: optionsState, selectedOptionIndex } = useAppSelector(
    (state) => state.alerts,
  );

  const dispatch = useAppDispatch();

  const performConfirm = useCallback(
    async (index: number) => {
      const onSelect = optionCallbacksRef.current[index];
      optionCallbacksRef.current = [];
      onCancelRef.current = null;
      if (onSelect) {
        await onSelect();
      }
      dispatch(
        setOptions({
          options: OptionsState.OFF,
          optionsMessage: null,
          optionItems: [],
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (
      optionCallbacksRef.current.length > 0 &&
      optionsState == OptionsState.SELECTED &&
      selectedOptionIndex !== null
    ) {
      void performConfirm(selectedOptionIndex);
    }
  }, [optionsState, selectedOptionIndex, performConfirm]);

  useEffect(() => {
    if (optionsState == OptionsState.OFF) {
      if (onCancelRef.current !== null) {
        void onCancelRef.current();
      }
      optionCallbacksRef.current = [];
    }
  }, [optionsState]);

  return useCallback(
    (
      optionsMessage: string,
      optionItems: CustomOption[],
      onCancel?: OnSelect,
    ) => {
      optionCallbacksRef.current = optionItems.map((option) => option.onSelect);
      onCancelRef.current = onCancel || null;

      dispatch(
        setOptions({
          options: OptionsState.WAITING_FOR_SELECTION,
          optionsMessage,
          optionItems: optionItems.map((option) => ({
            text: option.text,
            variant: option.variant,
          })),
        }),
      );
    },
    [dispatch],
  );
}
